import { useMemo } from 'react';
import {
  FormTemplateRequest,
  FormTemplateResponse,
} from '@weave/schema-gen-ts/dist/schemas/forms-digital/weave_digital_forms.pb';
import { FormsV1 } from '@frontend/api-digital-forms';
import { http } from '@frontend/fetch';
import { DEFAULT_DFX_HTTP_OPTIONS } from '../../constants';
import { parseJsonOr } from '../../helpers/json-helpers';
import { SchemaForm } from '../../page-contents/filling-experience/components/dynamic-form-container/types';

interface FormTemplateSchemaResult extends Omit<FormTemplateResponse, 'data'> {
  data: SchemaForm[];
}

const removePaymentSections = (forms: SchemaForm[]): SchemaForm[] =>
  forms.map((form) => {
    const paymentSections = Object.entries(form.sections).filter(([, section]) => section.is_payment_field_present);
    const paymentSectionIds = new Set(paymentSections.map(([sectionId]) => sectionId));
    const paymentFieldIds = new Set(paymentSections.flatMap(([, section]) => section.fields));

    if (paymentSectionIds.size === 0) return form;

    return {
      ...form,
      form: {
        ...form.form,
        sections: form.form.sections.filter((sectionId) => !paymentSectionIds.has(sectionId)),
      },
      sections: Object.fromEntries(
        Object.entries(form.sections).filter(([sectionId]) => !paymentSectionIds.has(sectionId))
      ),
      fields: Object.fromEntries(Object.entries(form.fields).filter(([fieldId]) => !paymentFieldIds.has(fieldId))),
    };
  });

/** Previews keep payment sections (they render a static mock); real public/kiosk forms drop them. */
export const useGetFormTemplate = (args: FormTemplateRequest, { isPreviewMode = false } = {}) => {
  const {
    data: formTemplate,
    error,
    ...query
  } = FormsV1.Queries.useGetFormTemplateQuery({
    request: args,
    httpOptions: DEFAULT_DFX_HTTP_OPTIONS,
  });

  const jsonString = formTemplate?.data ?? '[]';

  const result = useMemo<FormTemplateSchemaResult | undefined>(() => {
    if (!formTemplate) {
      return undefined;
    }

    const forms = parseJsonOr<SchemaForm[]>(jsonString, []);
    const isPublicForm = !args.qwe && !isPreviewMode;

    return {
      ...formTemplate,
      data: isPublicForm ? removePaymentSections(forms) : forms,
    };
  }, [args.qwe, isPreviewMode, jsonString, formTemplate]);

  const isHttpError = http.isHttpError(error);
  const status = isHttpError ? error.status ?? 400 : undefined;

  const isNotRetryableError = status !== undefined && status >= 400 && status < 500 && status !== 408 && status !== 429;

  // True when the form data has no renderable content. Treated as an "unable to fetch" state.
  const isEmptyForm = !!result && result.data.every((f) => f.form.sections.length === 0);

  return {
    formTemplate: result,
    error,
    isNotRetryableError,
    isEmptyForm,
    ...query,
  };
};
